import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import './App.css';
// The header below hand-rolls the avatar markup with these class names, but
// nothing imported their stylesheet, so the initial rendered as bare text
// running into the email address. Invisible until now because every lawyer
// signs in with Google and has a photo; invited tenant staff do not.
import './components/UserAvatar/UserAvatar.css';
import LoginPage from './components/LoginPage/LoginPage';
import Dashboard from './components/Dashboard/Dashboard';
import CasesPage from './components/CasesPage/CasesPage';
import CaseDetailPage from './components/CaseDetailPage/CaseDetailPage';
import SupportCenterPage from './components/SupportCenterPage';
import SupportTicketDetailPage from './components/SupportTicketDetailPage';
import ReferralsDashboard from './components/ReferralsDashboard';
import LegacyCaseRedirect from './components/LegacyCaseRedirect';
import FinancasQueuePage from './components/FinancasQueuePage';
import DeletionRequestsPage from './components/DeletionRequestsPage';
import DeletionRequestsNavLink from './components/DeletionRequestsNavLink';
import TenantCasesPage from './components/TenantCasesPage';
import TenantInvitePage from './components/TenantInvitePage';
import TenantSettingsPage from './components/TenantSettingsPage';
import TenantBillingPage from './components/TenantBillingPage';
import TenantTeamPage from './components/TenantTeamPage';
import { partnerGuideUrl } from './utils/partnerGuide';
import TenantAccessCodesPage from './components/TenantAccessCodesPage';
import MarketplaceLayout from './components/Marketplace/MarketplaceLayout';
import PartnerBoardPage from './components/Marketplace/PartnerBoardPage';
import PartnerRequestPage from './components/Marketplace/PartnerRequestPage';
import PartnerMyWorkPage from './components/Marketplace/PartnerMyWorkPage';
import AdminRequestsPage from './components/Marketplace/AdminRequestsPage';
import AdminBoardPage from './components/Marketplace/AdminBoardPage';
import AdminRequestDetailPage from './components/Marketplace/AdminRequestDetailPage';
import AdminFirmsPage from './components/Marketplace/AdminFirmsPage';
import { CertifierLayout, CertificationCasePage, CertificationQueuePage, CertificationStatsPage, CertifierDetailsPage } from './components/CertificationPage';

// Roles from GET /auth/me. The marketplace flags are separate from is_partner:
// a marketplace firm user gets is_partner false and is_lawyer false.
interface UserRoles {
  is_lawyer: boolean;
  is_partner: boolean;
  is_general_user: boolean;
  /** Staff of a white-label partner. Mutually exclusive with the others. */
  is_tenant_user?: boolean;
  /**
   * admin may change branding and invite colleagues; member may not. Only
   * decides what the nav offers. The backend refuses regardless, so this
   * stops us rendering a door that does not open.
   */
  tenant_role?: 'admin' | 'member' | null;
  /** accessCodes: the partner pays for their customers, who get in with a code. */
  tenant?: { id: string; name: string; accessCodes?: boolean } | null;
  is_marketplace_partner?: boolean;
  is_marketplace_admin?: boolean;
  /** An outside lawyer who certifies signed POAs. Mutually exclusive with the others. */
  is_certifier?: boolean;
  /** Feature flags from the lawyer backend. Missing on older backends: off. */
  features?: { customer_requests?: boolean; oa_certification?: boolean };
}

function App() {
  const { currentUser, loading: authLoading, logout } = useAuth();
  const [roles, setRoles] = useState<UserRoles | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthorization = async () => {
      if (!currentUser) {
        setIsAuthorized(false);
        return;
      }

      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const userRoles = await response.json();
          setRoles(userRoles);
          if (
            userRoles.is_lawyer ||
            userRoles.is_partner ||
            userRoles.is_general_user ||
            userRoles.is_tenant_user ||
            userRoles.is_marketplace_partner ||
            userRoles.is_marketplace_admin ||
            userRoles.is_certifier
          ) {
            setIsAuthorized(true);
            setAuthError('');
          } else {
            setIsAuthorized(false);
            setAuthError('You are not authorized to access this application.');
            await logout();
          }
        } else {
          setIsAuthorized(false);
          setAuthError('You are not authorized to access this application.');
          await logout();
        }
      } catch (error) {
        setIsAuthorized(false);
        setAuthError('Could not connect to the server for authorization.');
        await logout();
      }
    };

    if (!authLoading) {
      checkAuthorization();
    }
  }, [currentUser, authLoading, logout]);

  if (authLoading) {
    return <div>Loading...</div>;
  }

  // Accepting an invite has to work for somebody with no account, which is
  // every invited person by definition. Placed before the authorisation gate
  // deliberately: the page signs them in itself and the backend still checks
  // the invite against the identity they end up with.
  if (window.location.pathname.startsWith('/tenant/invite/')) {
    return (
      <Routes>
        <Route path="/tenant/invite/:token" element={<TenantInvitePage />} />
      </Routes>
    );
  }

  if (!isAuthorized) {
    return <LoginPage error={authError} />;
  }

  // Marketplace firm users get their own slim shell with only the marketplace
  // routes. Every other path, including /, redirects to the board. The backend
  // enforces the same split; this keeps the UI from offering what it rejects.
  if (roles?.is_marketplace_partner) {
    return (
      <MarketplaceLayout>
        <Routes>
          <Route path="/marketplace" element={<PartnerBoardPage />} />
          <Route path="/marketplace/requests/:requestId" element={<PartnerRequestPage />} />
          <Route path="/marketplace/my-work" element={<PartnerMyWorkPage />} />
          <Route path="*" element={<Navigate to="/marketplace" replace />} />
        </Routes>
      </MarketplaceLayout>
    );
  }

  // A POA certifier gets one queue and the cases in it. Same reasoning as the
  // marketplace shell: the backend refuses everything else to them.
  if (roles?.is_certifier) {
    return (
      <CertifierLayout>
        <Routes>
          <Route path="/certifications" element={<CertificationQueuePage />} />
          <Route path="/certifications/statistics" element={<CertificationStatsPage />} />
          <Route path="/certifications/me" element={<CertifierDetailsPage />} />
          <Route path="/certifications/:caseId" element={<CertificationCasePage />} />
          <Route path="*" element={<Navigate to="/certifications" replace />} />
        </Routes>
      </CertifierLayout>
    );
  }

  // Every verified coepi.eu account is a marketplace admin (the backend
  // decides), with or without a lawyers doc.
  const isMarketplaceAdmin = Boolean(roles?.is_marketplace_admin);

  return (
    <div className="coepi-container">
      <header className="coepi-header">
        {/* Matches the document title, which has always said "Coepi Partner
            Portal". The bare wordmark read as the customer-facing brand, which
            is confusing in the one app that is not customer facing: lawyers,
            referral partners, marketplace firms and white-label tenants all
            sign in here. */}
        <div className="logo">Coepi Partner</div>
        <div className="user-profile">
          <div className="user-avatar">
            {currentUser?.photoURL ? (
              <img src={currentUser.photoURL} alt={currentUser.displayName || 'Partner'} />
            ) : (
              <div className="user-avatar-initial">
                {currentUser?.email?.charAt(0).toUpperCase() || 'P'}
              </div>
            )}
          </div>
          <span>{currentUser?.email}</span>
          <button onClick={logout} className="logout-button">Sign Out</button>
        </div>
      </header>
      <main className="coepi-main">
        <aside className="sidebar">
          <div className="welcome-message">
            {/* An eyebrow above the greeting, not a line below it. Sitting
                between the greeting and the nav, in paragraph type, it read as
                the first menu item: "IMIGRATA, Your cases, Branding, Team".
                It is the label for everything below it, so it goes on top and
                is styled as a label rather than as body text. */}
            {roles?.tenant && <p className="tenant-org">{roles.tenant.name}</p>}
            <h2>Welcome Back, {currentUser?.displayName || 'Partner'}.</h2>
          </div>
          <nav className="task-nav">
            <ul>
              {(roles?.is_lawyer || roles?.is_partner) && (
                <li><Link to="/">Dashboard</Link></li>
              )}
              {/* A tenant user gets this one entry and nothing else. The
                  backend refuses them everywhere anyway, but a nav item they
                  can click and be denied is a bad way to find that out. */}
              {roles?.is_tenant_user && (
                <li><Link to="/tenant/cases">Your cases</Link></li>
              )}
              {/* Only for a partner who pays for their customers: each one
                  gets a code instead of a price. Members see the list,
                  admins also make and withdraw codes. */}
              {roles?.is_tenant_user && roles?.tenant?.accessCodes && (
                <li><Link to="/tenant/access-codes">Access codes</Link></li>
              )}
              {/* Every tenant user, members included: the guide is how a new
                  colleague learns the product. It is its own site, signed in
                  with this same account. */}
              {roles?.is_tenant_user && (
                <li>
                  <a href={partnerGuideUrl()} target="_blank" rel="noopener noreferrer">
                    Partner guide
                  </a>
                </li>
              )}
              {/* Only an admin of the partner organisation. A member sees
                  their cases and nothing else. */}
              {roles?.is_tenant_user && roles?.tenant_role === 'admin' && (
                <>
                  <li><Link to="/tenant/settings">Branding</Link></li>
                  <li><Link to="/tenant/team">Team</Link></li>
                  <li><Link to="/tenant/billing">Billing</Link></li>
                </>
              )}
              {roles?.is_lawyer && (
                <>
                  <li><Link to="/cases">Cases</Link></li>
                  {/* Same page, pinned to white-label cases. A partner's
                      customer has never heard of Coepi, so knowing whose
                      case it is changes how the lawyer replies. */}
                  <li><Link to="/partner-cases">Partner cases</Link></li>
                  <li><Link to="/queue">Finanças queue</Link></li>
                </>
              )}
              {(roles?.is_partner || roles?.is_general_user) && (
                <li><Link to="/referrals">Referrals</Link></li>
              )}
              {roles?.is_lawyer && (
                <>
                  <li><Link to="/support">Support</Link></li>
                  <li><DeletionRequestsNavLink /></li>
                </>
              )}
              {isMarketplaceAdmin && (
                <>
                  <li><Link to="/marketplace/board">Marketplace board</Link></li>
                  <li><Link to="/marketplace/admin/requests">Marketplace triage</Link></li>
                </>
              )}
            </ul>
          </nav>
        </aside>
        <section className="task-view">
          <Routes>
            {roles?.is_tenant_user ? (
              <Route path="/" element={<Navigate to="/tenant/cases" replace />} />
            ) : (roles?.is_lawyer || roles?.is_partner) ? (
              <Route path="/" element={<Dashboard />} />
            ) : isMarketplaceAdmin ? (
              <Route path="/" element={<Navigate to="/marketplace/board" replace />} />
            ) : (
              <Route path="/" element={<Navigate to="/referrals" replace />} />
            )}
            {roles?.is_tenant_user && (
              <Route
                path="/tenant/cases"
                element={
                  <TenantCasesPage
                    clientsPay={!roles?.tenant?.accessCodes}
                    isAdmin={roles?.tenant_role === 'admin'}
                  />
                }
              />
            )}
            {roles?.is_tenant_user && roles?.tenant?.accessCodes && (
              <Route
                path="/tenant/access-codes"
                element={<TenantAccessCodesPage isAdmin={roles?.tenant_role === 'admin'} />}
              />
            )}
            {roles?.is_tenant_user && roles?.tenant_role === 'admin' && (
              <>
                <Route path="/tenant/settings" element={<TenantSettingsPage />} />
                <Route path="/tenant/team" element={<TenantTeamPage />} />
                <Route path="/tenant/billing" element={<TenantBillingPage />} />
              </>
            )}
            {roles?.is_lawyer && (
              <>
                <Route path="/cases" element={<CasesPage />} />
                <Route path="/partner-cases" element={<CasesPage scope="partner" />} />
                <Route path="/queue" element={<FinancasQueuePage />} />
                <Route path="/support" element={<SupportCenterPage />} />
                <Route path="/deletion-requests" element={<DeletionRequestsPage />} />
                <Route
                  path="/support/ticket/:ticketId"
                  element={<SupportTicketDetailPage canCreateMarketplaceRequest={isMarketplaceAdmin} />}
                />
              </>
            )}
            {(roles?.is_partner || roles?.is_general_user) && (
              <Route path="/referrals" element={<ReferralsDashboard />} />
            )}
            {/* Every /cases/* endpoint the case page calls depends on
                get_current_lawyer, so a referral partner only ever got 403s
                here. Gate the route the same way. */}
            {roles?.is_lawyer && (
              <>
                <Route
                  path="/case/:caseId"
                  element={
                    <CaseDetailPage
                      onStatusChange={() => {}}
                      customerRequestsEnabled={Boolean(roles?.features?.customer_requests)}
                      oaCertificationEnabled={Boolean(roles?.features?.oa_certification)}
                    />
                  }
                />
                <Route path="/cases/:caseId" element={<LegacyCaseRedirect />} />
              </>
            )}
            {isMarketplaceAdmin && (
              <>
                <Route path="/marketplace/board" element={<AdminBoardPage />} />
                <Route path="/marketplace/admin/requests" element={<AdminRequestsPage />} />
                <Route path="/marketplace/admin/requests/:requestId" element={<AdminRequestDetailPage />} />
                <Route path="/marketplace/admin/firms" element={<AdminFirmsPage />} />
              </>
            )}
          </Routes>
        </section>
      </main>
    </div>
  );
}

export default App;
